import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { ThemeService } from '../../services/theme/theme.service';
import { Themes } from '@sc/types';

import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { of, Subscription, switchMap } from 'rxjs';
import { RecordingService } from '../../services/recording/recording.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { NameService } from '../../services/name/name.service';
import { StatsService } from '../../services/stats/stats.service';
import { SCSubject } from '../../util/sc-subject.class';
import { UserService } from '../../services/user/user.service';

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
);
Chart.defaults.font = {
  family: "'MoriGothic-Regular', 'MoriGothic', 'Helvetica', 'Arial', sans-serif",
  lineHeight: 1.2,
  size: 12,
  style: 'normal',
};
Chart.defaults.color = '#444';

Chart.defaults.parsing = false;
Chart.defaults.interaction.mode = 'nearest';
Chart.defaults.interaction.intersect = false;
Chart.defaults.scales.time.ticks.display = false;
Chart.defaults.scale.ticks.maxTicksLimit = 7;
Chart.defaults.datasets.line.cubicInterpolationMode = 'monotone';
Chart.defaults.datasets.line.tension = 0.4;
Chart.defaults.datasets.line.spanGaps = 1000 * 10;
Chart.defaults.datasets.line.animation = {
  duration: 400,
};

@Component({
  selector: 'sc-stats-for-nerds',
  templateUrl: './stats-for-nerds.component.html',
  styleUrls: ['./stats-for-nerds.component.scss'],
})
export class StatsForNerdsComponent implements OnInit, OnDestroy, OnChanges {
  @Output() closeSidebar = new EventEmitter<boolean>();
  @Input() participantID: string;
  @Input() appearance: string;

  @ViewChild('totalBitrateCanvas') totalBitrateCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('availablePieCanvas') availablePieCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('packetLossCanvas') packetLossCanvas: ElementRef<HTMLCanvasElement>;

  participantID$ = new SCSubject<string>(null, true);
  localStats$ = this.statsService.connectionStats$;

  MAX_CHART_TIME = 1000 * 60 * 5;

  audioColor = { borderColor: '#8321ae', backgroundColor: '#A429D9' };
  videoColor = { borderColor: '#b35675', backgroundColor: '#e06c92' };
  mVideoColor = { borderColor: '#c24a04', backgroundColor: '#F25C05' };
  lVideoColor = { borderColor: '#cc9f00', backgroundColor: '#FFC700' };
  screenshareColor = { borderColor: '#5603cc', backgroundColor: '#6C04FF' };
  availableColor = { borderColor: '#339295', backgroundColor: '#40b7ba' };
  packetLossColor = { borderColor: '#cc0000', backgroundColor: '#ff0000' };

  extraColors = [
    { borderColor: '#0cafb7', backgroundColor: '#0FDBE5' },
    { borderColor: '#c50057', backgroundColor: '#F6006D' },
    { borderColor: '#4a9a69', backgroundColor: '#5CC083' },
    { borderColor: '#cc5c5c', backgroundColor: '#FF7373' },
    { borderColor: '#a32a83', backgroundColor: '#CC35A4' },
    { borderColor: '#853c2c', backgroundColor: '#A64B37' },
  ];

  availableBitrate = {
    chart: null,
  };
  availablePie = {
    chart: null,
  };
  packetLoss = {
    totalLost: new Map<string, number>(),
    chart: null,
  };

  recordingActive = false;
  recordingID = '';
  screenID = '';
  participantName: string;
  hasAvailableBitrate = false;
  summaryMap = new Map<string, any>();

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private nameService: NameService,
    private recordingService: RecordingService,
    private sessionsService: SessionsService,
    private statsService: StatsService,
    private userService: UserService,
    private studioSidebarService: StudioSidebarService,
    private themeService: ThemeService
  ) {}

  ngOnInit() {
    setTimeout(async () => {
      await this.buildCharts();
      this.setupChartUpdates();
    }, 0);
    this.setupTheme();
    this.setupRecording();
    this.setupName();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.participantID) {
      if (this.participantID) {
        this.participantID$.next(this.participantID);
      }
    }
  }

  setupName() {
    const sub = this.participantID$
      .pipe(
        switchMap((id) => {
          if (id && id !== this.userService.activeUser$.value?.uid) {
            return this.nameService.getName(this.sessionsService.studioSession$.value.sessionID, id);
          } else {
            return of(null);
          }
        })
      )
      .subscribe((name) => {
        this.participantName = name;
      });

    this.subs.push(sub);
  }

  setupRecording() {
    const sub = this.sessionsService.studioSession$
      .pipe(switchMap((session) => this.sessionsService.getAllRecordingsFromTake(session.sessionID, session.take)))
      .subscribe((recordings) => {
        if (this.recordingService.recording$.value) {
          const primary = recordings.find((r) => r.participantID === this.participantID && !r.screenRecording);
          this.recordingID = primary?.recordingID;
          const screen = recordings.find((r) => r.participantID === this.participantID && r.screenRecording);
          this.screenID = screen?.recordingID;
          if (this.recordingID) this.recordingActive = this.recordingService.recording$.value;
        } else {
          this.recordingActive = false;
          this.screenID = null;
          this.recordingID = null;
        }
      });
    this.subs.push(sub);
  }

  setupTheme() {
    const sub = this.themeService.theme$.subscribe((theme) => {
      if (theme === Themes.DARK) {
        Chart.defaults.color = '#ddd';
      } else {
        Chart.defaults.color = '#444';
      }
    });
    this.subs.push(sub);
  }

  newDS(label: string, options?: any) {
    let ds = {
      label,
      data: [],
      ...options,
    };
    if (!options.backgroundColor && !options.borderColor) {
      ds = { ...ds, ...this.color() };
    }
    return ds;
  }

  color(index?: number) {
    if (!index) index = Math.floor(Math.random() * this.extraColors.length);
    return this.extraColors[index % this.extraColors.length];
  }

  cleanData(data: any[]) {
    if (!data || data.length === 0) return;
    const time = new Date().getTime();
    if (data[0].x < time - this.MAX_CHART_TIME) {
      data.shift();
      this.cleanData(data);
    }
  }

  async buildCharts() {
    const totalBitrateConfig: any = {
      type: 'line',
      data: {
        datasets: [],
      },
      options: {
        datasets: { line: { pointRadius: 0 } },
        plugins: { title: { display: true, text: 'Used out of Available Bitrate' } },
        scales: {
          x: { type: 'time', title: { text: 'Time' } },
          y: {
            title: { text: 'Bitrate (bps)', display: true },
            ticks: {
              callback: (bps) => {
                if (bps >= 10000000) return `${Math.round(bps / 1000000)}M`;
                if (bps >= 1000000) return `${Math.round(bps / 100000) / 10}M`;
                else if (bps >= 10000) return `${Math.round(bps / 1000)}K`;
                else if (bps >= 1000) return `${Math.round(bps / 100) / 10}K`;
                else return bps;
              },
            },
            stacked: true,
          },
        },
      },
    };
    this.availableBitrate.chart = new Chart(this.totalBitrateCanvas.nativeElement, totalBitrateConfig);

    const availablePieConfig: any = {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderColor: [],
          },
        ],
        labels: [],
      },
      options: {
        plugins: { title: { display: false }, legend: { position: 'right' } },
        borderAlign: 'inner',
      },
    };
    this.availablePie.chart = new Chart(this.availablePieCanvas.nativeElement, availablePieConfig);

    const packetLossConfig: any = {
      type: 'bar',
      data: {
        datasets: [],
      },
      options: {
        plugins: {
          title: { display: true, text: 'Jitter & Packet Loss' },
          legend: { display: false },
        },
        datasets: { bar: { barThickness: 4 }, line: { pointRadius: 2 } },
        scales: {
          x: { type: 'time', title: { text: 'Time' } },
          y: {
            title: { text: 'Packets Lost', display: true },
          },
          yJitter: {
            type: 'linear',
            title: { text: 'Jitter (ms)', display: true },
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
          },
        },
      },
    };
    this.packetLoss.chart = new Chart(this.packetLossCanvas.nativeElement, packetLossConfig);
  }

  setupChartUpdates() {
    const sub = this.participantID$
      .pipe(
        switchMap((id) => {
          if (id) {
            return this.sessionsService.getSessionStats(null, id);
          } else {
            return this.localStats$;
          }
        })
      )
      .subscribe((stats) => {
        this.summaryMap = new Map();
        // Update Audio Bitrate Chart
        Object.keys(stats.audio).forEach((ssrc) => {
          const aStat = stats.audio[ssrc];
          const availDS = this.availableBitrate.chart.data.datasets;

          if (!availDS.find((d) => d.ssrc === ssrc)) {
            availDS.push(this.newDS('Audio', { ...this.audioColor, ssrc, fill: true, order: 1 }));
          }
          const aDS = availDS.find((d) => d.ssrc === ssrc);
          const data = aDS.data;

          const newData = aStat.data?.filter((d) => !data.length || d.x > data[data.length - 1].x);
          newData?.forEach((d) => data.push({ x: d.x, y: d.bitrate }));

          this.summaryMap.set('Audio', { color: this.audioColor, data: aStat.bitrate });

          this.updatePacketLoss('Audio', ssrc, this.audioColor, aStat);
        });

        // Update Video Bitrate Chart
        Object.keys(stats.video).forEach((ssrc) => {
          const vStat = stats.video[ssrc];
          const availDS = this.availableBitrate.chart.data.datasets;
          let label = 'Video';
          let color = this.videoColor;
          let order = 4;

          if (vStat.mid === 'screenshare' || vStat.contentType === 'screenshare') {
            label = 'Screenshare';
            color = this.screenshareColor;
            order = 5;
          }
          if (vStat.rid) {
            label += ' (' + vStat.rid.toUpperCase() + ')';
            if (vStat.rid === 'm') {
              color = this.mVideoColor;
              order = 3;
            }
            if (vStat.rid === 'l') {
              color = this.lVideoColor;
              order = 2;
            }
          }

          if (!availDS.find((d) => d.ssrc === ssrc)) {
            availDS.push(this.newDS(label, { ...color, ssrc, fill: true, order }));
          }
          const aDS = availDS.find((d) => d.ssrc === ssrc);
          const data = aDS.data;

          const newData = vStat.data?.filter((d) => !data.length || d.x > data[data.length - 1].x);
          newData?.forEach((d) => data.push({ x: d.x, y: d.bitrate }));

          this.summaryMap.set(label, { color, data: vStat.bitrate });

          this.updatePacketLoss(label, ssrc, color, vStat);
        });

        // Update Available Bitrate Chart
        if (stats.network.availableOutgoingBitrate) {
          const availDS = this.availableBitrate.chart.data.datasets;

          if (!availDS.find((d) => d.ssrc === 'available')) {
            availDS.push(
              this.newDS('Available', {
                stack: 'total',
                ssrc: 'available',
                borderDash: [10],
                ...this.availableColor,
              })
            );
          }

          const ds = availDS.find((d) => d.ssrc === 'available');
          const data = ds.data;
          const newData = stats.network.data?.filter((d) => !data.length || d.x > data[data.length - 1].x);
          newData?.forEach((d) => data.push({ x: d.x, y: d.available }));

          const pieData = this.availablePie.chart.data;
          const pieDS = pieData.datasets[0];

          let used = 0;
          this.summaryMap.forEach((summary) => {
            used += summary.data;
          });

          this.summaryMap.set('Available', {
            color: this.availableColor,
            data: stats.network.availableOutgoingBitrate - used,
          });

          this.summaryMap.forEach((summary, label) => {
            const i = pieData.labels.findIndex((d) => d === label);
            if (i === -1) {
              pieData.labels.push(label);
              pieDS.data.push(summary.data);
              pieDS.backgroundColor.push(summary.color.backgroundColor);
              pieDS.borderColor.push(summary.color.borderColor);
            } else {
              pieDS.data[i] = summary.data;
            }
          });
          pieData.labels.forEach((label, i) => {
            if (!this.summaryMap.has(label)) {
              pieDS.data.splice(i, 1);
              pieDS.backgroundColor.splice(i, 1);
              pieDS.borderColor.splice(i, 1);
              pieData.labels.splice(i, 1);
            }
          });

          this.hasAvailableBitrate = true;
        }

        this.availableBitrate.chart.data.datasets.forEach((ds) => this.cleanData(ds.data));
        this.availableBitrate.chart.update();
        this.packetLoss.chart.data.datasets.forEach((ds) => this.cleanData(ds.data));
        this.packetLoss.chart.update();
        this.availablePie.chart.data.datasets.forEach((ds) => this.cleanData(ds.data));
        this.availablePie.chart.update();
      });
    this.subs.push(sub);
  }

  updatePacketLoss(label: string, ssrc: string, color: any, stat: any) {
    // Update Packet Loss Chart Stats
    if (stat.remote?.packetsLost || stat.remote?.packetLost) {
      const lossLabel = label + ' Packet Loss';
      const plDS = this.packetLoss.chart.data.datasets;
      if (!plDS.find((d) => d.ssrc === ssrc && d.label === lossLabel)) {
        const offset = new Date().getTime() - stat.remote.timestamp;
        plDS.push(this.newDS(lossLabel, { ...this.packetLossColor, ssrc, offset }));
      }

      const ds = plDS.find((d) => d.ssrc === ssrc && d.label === lossLabel);
      const data = ds.data;
      const newData = stat.remote.data?.filter((d) => !data.length || d.x + ds.offset > data[data.length - 1].x);
      newData?.forEach((d) => {
        if (d.packetsLost) {
          data.push({ x: d.x + ds.offset, y: d.packetsLost });
        }
      });
    }

    if (stat.remote?.jitter) {
      const jitterLabel = label + ' Jitter';
      const jDS = this.packetLoss.chart.data.datasets;
      if (!jDS.find((d) => d.ssrc === ssrc && d.label === jitterLabel)) {
        const offset = new Date().getTime() - stat.remote.timestamp;
        jDS.push(this.newDS(jitterLabel, { ...color, ssrc, type: 'line', yAxisID: 'yJitter', offset }));
      }

      const ds = jDS.find((d) => d.ssrc === ssrc && d.label === jitterLabel);
      const data = ds.data;
      const newData = stat.remote.data?.filter((d) => !data.length || d.x + ds.offset > data[data.length - 1].x);
      newData?.forEach((d) => data.push({ x: d.x + ds.offset, y: d.jitter * 1000 }));
    }
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
